import { useMemo } from "react";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@project/components/ui/card";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@project/components/ui/collapsible";
import {
  ChevronDown,
  RotateCcw,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "@project/lib/utils";
import {
  DONATION_FILTER_ALL,
  countActiveDonationFilters,
  donationListFilterInitialState,
  type DonationFilterOption,
  type DonationListFilterState,
} from "./donation-list-filter-utils";

const STATUS_OPTIONS = [
  { value: DONATION_FILTER_ALL, label: "All status" },
  { value: "success", label: "Success" },
  { value: "created", label: "Created" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "captured", label: "Captured" },
] as const;

type DonationListFiltersProps = {
  filters: DonationListFilterState;
  onFiltersChange: (filters: DonationListFilterState) => void;
  filtersExpanded: boolean;
  onFiltersExpandedChange: (open: boolean) => void;
  donationTypes: DonationFilterOption[];
  paymentModes: DonationFilterOption[];
  schemes: DonationFilterOption[];
};

export function DonationListFilters({
  filters,
  onFiltersChange,
  filtersExpanded,
  onFiltersExpandedChange,
  donationTypes,
  paymentModes,
  schemes,
}: DonationListFiltersProps) {
  const panelFilterCount = useMemo(
    () => countActiveDonationFilters(filters),
    [filters]
  );

  const patch = (partial: Partial<DonationListFilterState>) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const handleReset = () => {
    onFiltersChange(donationListFilterInitialState);
  };

  const hasActiveFilters =
    panelFilterCount > 0 || filters.search.trim().length > 0;

  return (
    <Collapsible open={filtersExpanded} onOpenChange={onFiltersExpandedChange}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="donation-list-search"
            placeholder="Search name, email, phone, campaign..."
            value={filters.search}
            onChange={e => patch({ search: e.target.value })}
            className="bg-muted/40 h-10 rounded-xl border-transparent pl-10"
            aria-label="Search donations"
          />
        </div>

        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-10 shrink-0 gap-2 rounded-xl px-4 font-semibold"
            aria-expanded={filtersExpanded}
            aria-controls="donation-list-filters-panel"
          >
            <span className="bg-primary/10 flex h-7 w-7 items-center justify-center rounded-md">
              <SlidersHorizontal className="h-4 w-4 text-primary" />
            </span>
            Filters
            {panelFilterCount > 0 && (
              <Badge className="h-5 min-w-5 border-0 bg-primary px-1.5 text-[11px] text-primary-foreground hover:bg-primary">
                {panelFilterCount}
              </Badge>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                filtersExpanded && "rotate-180"
              )}
            />
          </Button>
        </CollapsibleTrigger>

        {hasActiveFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-10 shrink-0 gap-2 rounded-xl text-muted-foreground"
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        )}
      </div>

      <CollapsibleContent
        id="donation-list-filters-panel"
        className="data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 overflow-visible"
      >
        <Card className="mb-4 overflow-visible rounded-2xl shadow-sm">
          <CardHeader className="space-y-1 pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <span className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-md">
                <SlidersHorizontal className="h-4 w-4 text-primary" />
              </span>
              Filter donations
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-visible pt-0">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="donation-filter-message"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Donor message
                </Label>
                <Input
                  id="donation-filter-message"
                  placeholder="Filter by donor message..."
                  value={filters.message}
                  onChange={e => patch({ message: e.target.value })}
                  className="bg-muted/40 h-10 rounded-xl border-transparent"
                />
              </div>
              <FilterSelect
                id="donation-filter-type"
                label="Donation type"
                value={filters.donation_type}
                onValueChange={v => patch({ donation_type: v })}
                options={[
                  { value: DONATION_FILTER_ALL, label: "All types" },
                  ...donationTypes,
                ]}
              />
              <FilterSelect
                id="donation-filter-status"
                label="Payment status"
                value={filters.status}
                onValueChange={v => patch({ status: v })}
                options={STATUS_OPTIONS.map(o => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
              <FilterSelect
                id="donation-filter-payment-mode"
                label="Payment mode"
                value={filters.payment_mode}
                onValueChange={v => patch({ payment_mode: v })}
                options={[
                  { value: DONATION_FILTER_ALL, label: "All modes" },
                  ...paymentModes,
                ]}
              />
              <FilterSelect
                id="donation-filter-scheme"
                label="Campaign (scheme)"
                value={filters.scheme}
                onValueChange={v => patch({ scheme: v })}
                options={[
                  { value: DONATION_FILTER_ALL, label: "All campaigns" },
                  ...schemes,
                ]}
              />
            </div>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}

function FilterSelect({
  id,
  label,
  value,
  onValueChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          id={id}
          className="bg-muted/40 h-10 w-full rounded-xl border-transparent"
        >
          <SelectValue placeholder={`All ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map(opt => (
            <SelectItem key={`${id}-${opt.value}`} value={opt.value}>
              <span className="block max-w-[240px] truncate">{opt.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
